import { useState, useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { Typography, makeStyles } from '@material-ui/core';
import PageContainer from '../../shared/pageContainer';
import OrganizationSettingsForm from '../../shared/organizationSettingsForm';
import AlertDialog from '../../shared/alertDialog';
import { API_ABSENCE_SETS } from '../../../constants/apiRoutes';
import { APP_PAGES } from '../../../constants/pages';
import { ORG_SETTINGS_NAVIGATION_ITEMS } from '../../../constants/organizationSettings';
import { PERMISSION_CODES } from '../../../constants/permissions';
import http from '../../../utility/http';
import { showSuccessMessage } from '../../../utility/uiUtils';
import { useTranslations } from '../../../utility/useTranslations';
import { updateOrganizationSettings } from '../../../store/modules/organizationSettings';
import { ABSENCE_TRANSPARENCY } from '../../shared/organizationSettingsForm/layout';
import { isArrayEmpty } from '../../../utility/helpers';
import { hasAuthPermissionCheck } from '../../../utility/permissions';

const useStyles = makeStyles(({ spacing }) => ({
  description: {
    marginBottom: spacing(6),
  },
}));

const OrganizationSettingsFeaturesPage = ({
  navigate,
  dispatch,
  accessControl,
  organizationSettings,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(
    APP_PAGES.ORGANIZATION_SETTINGS_FEATURES
  );

  const [isInfoDialogOpened, setIsInfoDialogOpened] = useState(false);

  const canAccessPermissions = hasAuthPermissionCheck(
    PERMISSION_CODES.SETTINGS_PERMISSIONS_MANAGE,
    accessControl
  );

  const canAccessDocuments = hasAuthPermissionCheck(
    PERMISSION_CODES.SETTINGS_DOCUMENTS_MANAGE,
    accessControl
  );

  const canAccessProfileInfo = hasAuthPermissionCheck(
    PERMISSION_CODES.SETTINGS_PERSON_FIELDS_INFO_MANAGE,
    accessControl
  );

  const navigationItems = useMemo(() => {
    return ORG_SETTINGS_NAVIGATION_ITEMS.reduce((acc, navItem) => {
      if (
        (!canAccessPermissions && navItem.translationKey === 'permissions') ||
        (!canAccessProfileInfo && navItem.translationKey === 'profile-info') ||
        (!canAccessDocuments && navItem.translationKey === 'documents')
      ) {
        return acc;
      }
      return [
        ...acc,
        {
          ...navItem,
          name: translations.navigationItems[navItem.translationKey],
        },
      ];
    }, []);
  }, [
    translations.navigationItems,
    canAccessPermissions,
    canAccessProfileInfo,
    canAccessDocuments,
  ]);

  const onCloseInfoDialog = () => setIsInfoDialogOpened(false);

  const onConfirmInfoDialog = () => {
    navigate(`/absence/settings`);
  };

  const handleUpdateSettings = useCallback(
    async (updatedValues, initialValues) => {
      let hasAbsenceSets = false;

      if (
        !initialValues[ABSENCE_TRANSPARENCY] &&
        updatedValues[ABSENCE_TRANSPARENCY]
      ) {
        const { data } = await http.get(API_ABSENCE_SETS);
        hasAbsenceSets = !isArrayEmpty(data);
      }

      await updateOrganizationSettings(dispatch, updatedValues);
      showSuccessMessage(translations.settingSavedMessage);

      if (
        !initialValues[ABSENCE_TRANSPARENCY] &&
        updatedValues[ABSENCE_TRANSPARENCY] &&
        !hasAbsenceSets
      ) {
        setIsInfoDialogOpened(true);
      }
    },
    [dispatch, translations]
  );

  return (
    <PageContainer
      {...rest}
      translations={translations}
      accessControl={accessControl}
      organizationSettings={organizationSettings}
      navigate={navigate}
      navigationItems={navigationItems}
      shouldPassProps={false}
    >
      <div>
        <Typography className={classes.description} variant="body2">
          {translations.description}
        </Typography>
        <OrganizationSettingsForm
          translations={translations}
          initialValues={organizationSettings}
          updateOrganizationSettings={handleUpdateSettings}
        />
        <AlertDialog
          translations={translations.defaultAbsenceSetDialog}
          isOpened={isInfoDialogOpened}
          onClose={onCloseInfoDialog}
          onConfirm={onConfirmInfoDialog}
        />
      </div>
    </PageContainer>
  );
};

OrganizationSettingsFeaturesPage.propTypes = {
  accessControl: PropTypes.object.isRequired,
  organizationSettings: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default memo(OrganizationSettingsFeaturesPage);
